import React, { useState } from "react";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Button, Card, CardBody, CardHeader, Spinner } from "reactstrap";
import {
    currencyFormatWithAlert,
    currencyFormatWithoutDecimal,
    numberFormat
} from "../../helpers/api_helper";

const EXPORT_WIDTH = 1000; // fixed render width (px) so the PDF looks the same on every screen
const MARGIN = 10; // mm
const SCALE = 2;

const CheckListingPopup = ({ data, currency, selectedCheck, exportId = "check-listing-export" }) => {
    const [exporting, setExporting] = useState(false);

    const handleExportPDF = async () => {
        setExporting(true);
        let wrapper;
        try {
            const source = document.getElementById(exportId);
            if (!source) throw new Error(`Element with id ${exportId} not found`);

            // Clone off-screen at a fixed width, so popup width/scroll can't cut the content
            wrapper = document.createElement("div");
            wrapper.style.cssText = `position:absolute;left:-10000px;top:0;width:${EXPORT_WIDTH}px;background:#fff;`;
            const clone = source.cloneNode(true);
            clone.removeAttribute("id");
            clone.querySelectorAll(".table-responsive").forEach((el) => (el.style.overflow = "visible"));
            wrapper.appendChild(clone);
            document.body.appendChild(wrapper);

            // Safe page-break positions (bottom of each table row / card), in canvas px
            const top = wrapper.getBoundingClientRect().top;
            const breaks = [...clone.querySelectorAll("tr, .card")]
                .map((el) => Math.round((el.getBoundingClientRect().bottom - top) * SCALE))
                .sort((a, b) => a - b);

            const canvas = await html2canvas(wrapper, {
                scale: SCALE,
                backgroundColor: "#ffffff",
                useCORS: true,
                logging: false,
                windowWidth: EXPORT_WIDTH,
            });
            if (!canvas.width || !canvas.height) throw new Error("Content could not be captured");

            const pdf = new jsPDF("portrait", "mm", "a4");
            const usableW = pdf.internal.pageSize.getWidth() - MARGIN * 2;
            const usableH = pdf.internal.pageSize.getHeight() - MARGIN * 2;
            const pageH = Math.floor((canvas.width * usableH) / usableW); // A4 page height in canvas px

            let y = 0;
            while (y < canvas.height - 1) {
                const limit = y + pageH;
                const fits = breaks.filter((b) => b > y + pageH * 0.4 && b <= limit);
                const end = limit >= canvas.height ? canvas.height : fits.length ? Math.max(...fits) : limit;
                const sliceH = end - y;

                const page = document.createElement("canvas");
                page.width = canvas.width;
                page.height = sliceH;
                const ctx = page.getContext("2d");
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, page.width, page.height);
                ctx.drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

                if (y > 0) pdf.addPage();
                pdf.addImage(
                    page.toDataURL("image/jpeg", 0.95),
                    "JPEG",
                    MARGIN,
                    MARGIN,
                    usableW,
                    (sliceH * usableW) / canvas.width
                );
                y = end;
            }

            const checkNumber = data?.itemData?.check || "details";
            pdf.save(`Check-${checkNumber}`.replace(/[\\/:*?"<>|]/g, "-") + ".pdf");
        } catch (error) {
            console.error("Error exporting check details PDF:", error);
            window.alert("PDF export failed. Please try again.");
        } finally {
            if (wrapper && wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
            setExporting(false);
        }
    };

    const header = document.getElementById("check-listing-header");

    return (
        <React.Fragment>
            {header && createPortal(
                <React.Fragment>
                    <span className="text-nowrap">Check #{selectedCheck}</span>
                    <Button className="ms-auto" color="primary" onClick={handleExportPDF} disabled={exporting}>
                        {exporting ? <Spinner size="sm" /> : "Export PDF"}
                    </Button>
                </React.Fragment>,
                header
            )}
 
            <div id={exportId}>
            {/* Top Card */}
            <Card>
                <CardHeader className="d-flex flex-column flex-md-row justify-content-between bg-primary gap-3">
                    {/* Left Section */}
                    <div>
                        {/* Top Row */}
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <p className="text-white mb-0 text-nowrap">
                                Cover : <span className="bg-white text-black px-2 rounded-1">{numberFormat(data.itemData.covers)}</span>
                            </p>
                            <p className="text-white mb-0 text-nowrap">
                                Avg Check : <span className="bg-white text-black px-2 rounded-1">{currencyFormatWithAlert(data.itemData.avg_check, currency)}</span>
                            </p>
                            <p className="text-white mb-0 text-nowrap">
                                Revenue : <span className="bg-white text-black px-2 rounded-1">{currencyFormatWithoutDecimal(data.itemData.revenue, currency)}</span>
                            </p>
                            <p className="text-white mb-1">Server :{" "}
                                <span className="bg-white text-black px-2 rounded-1">
                                    {data.itemData.employee}
                                </span>
                            </p>
                        </div>

                        {/* Bottom Row */}
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <p className="text-white mb-0 text-nowrap">
                                Opening Time :{" "}
                                <span className="bg-white text-black px-2 rounded-1">
                                    {data.itemData.open}
                                </span>
                            </p>
                            <p className="text-white mb-0 text-nowrap">
                                Closing Time :{" "}
                                <span className="bg-white text-black px-2 rounded-1">
                                    {data.itemData.close}
                                </span>
                            </p>
                        </div>
                    </div>


                </CardHeader>

                <CardBody>
                    <div className="table-responsive">
                        <table className="table table-borderless align-middle text-center mb-0">
                            <thead className="text-muted table-light">
                                <tr>
                                    <th>Item Name</th>
                                    <th>Category</th>
                                    <th>Quantity</th>
                                    <th>Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.allItems && data.allItems.length > 0 ? (
                                    data.allItems.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.item}</td>
                                            <td>
                                                <span className="badge bg-light text-dark">
                                                    {item.category || "-"}
                                                </span>
                                            </td>
                                            <td>{numberFormat(item.qty)}</td>
                                            <td>{currencyFormatWithoutDecimal(item.revenue, currency)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted">
                                            No items found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardBody>

            </Card>

            {/* Discount Card */}
            <Card className="mt-3">
                <CardHeader className="bg-primary">
                    <h5 className="text-white mb-0">Discount</h5>
                </CardHeader>
                <CardBody>
                    <div className="table-responsive">
                        <table className="table table-borderless align-middle text-center mb-0">
                            <thead className="text-muted table-light">
                                <tr>
                                    <th>Item Name</th>
                                    <th>Category</th>
                                    <th>Quantity</th>
                                    <th>Discount Name</th>
                                    <th>Discount Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.discountedItems && data.discountedItems.length > 0 ? (
                                    data.discountedItems.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.item}</td>
                                            <td>
                                                <span className="badge bg-light text-dark">
                                                    {item.category || "N/A"}
                                                </span>
                                            </td>
                                            <td>{numberFormat(item.qty)}</td>
                                            <td>{item.discount_name}</td>
                                            <td>{currencyFormatWithoutDecimal(item.discount, currency)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted">
                                            No discounted items found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardBody>
            </Card>


            {/* Summary Table */}
            <div className="table-responsive mt-3">
                <table className="table text-center m-0">
                    <tbody>
                        {/* ✅ Loop through masterClassBreakup */}
                        {data?.masterClassBreakup &&
                            Object.entries(data.masterClassBreakup).map(([key, value], index) => (
                                <tr key={index}>
                                    <td className="fw-bold w-50 text-start table-light">
                                        {key} Sales
                                    </td>
                                    <td className="w-50 text-start fw-semibold">
                                        {currencyFormatWithAlert(value, currency)}

                                    </td>
                                </tr>
                            ))}

                        {/* ✅ Discount Row */}
                        <tr>
                            <td className="fw-bold w-50 text-start table-light">Discount</td>
                            <td className="w-50 text-start fw-semibold">

                                {data?.discountedItems?.length
                                    ? currencyFormatWithAlert(data.discountedItems
                                        .reduce((acc, item) => acc + (Number(item.discount) || 0), 0)
                                        .toFixed(2), currency)
                                    : currencyFormatWithAlert("0.00", currency)}
                            </td>
                        </tr>

                        {/* ✅ Total Row */}
                        <tr>
                            <td className="fw-bold w-50 text-start table-light">Total</td>
                            <td className="w-50 text-start fw-semibold ">
                                {currencyFormatWithoutDecimal(data?.itemData?.revenue, currency)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        </React.Fragment>
    );
};

export default CheckListingPopup;